'use client';

/**
 * Compact balance bar for the multicade surfaces — the catalogue root header and
 * the store-tab header.
 *
 * Shows CASH and TICKETZ only, each as a currency ICON + value (per
 * SkillzPartnerPrototype_v2's wallet strip). **Z is deliberately excluded**: Z
 * (practice currency) is game-scoped — earned, spent and displayed per game — so
 * it carries no meaning on a cross-game multicade surface. Cash and ticketz are
 * system-wide account balances, which is why the native Skillz App's store
 * wrapper passes `shouldShowTicketz: true`
 * (`skillzapp/screens/StoreScreen/index.tsx`) while every Z surface stays behind
 * game-level client properties.
 *
 * Presentational only — no fetching, no store subscription. Callers pass whatever
 * they already know; anything they do not know is simply not rendered.
 */

import {I18n} from '@sdk-mobile';

interface MulticadeBalanceBarProps {
  /** USD cash balance. Omit when unknown (signed out / fetch failed). */
  cashBalance?: number;
  /** Ticketz balance. Omit when unknown (signed out / fetch failed). */
  ticketzBalance?: number;
  /**
   * 'onDark' (default) — white value text for a dark/primary header bar.
   * 'onLight' — dark value text for the light balance strip under a partner's
   * custom header (e.g. the Lycos `walletBg` strip in the prototype).
   */
  tone?: 'onDark' | 'onLight';
}

// Formatting mirrors MainHeader's `formatValue`: currency for cash, abbreviated
// for ticketz. portal-play has no shared money formatter (UserBalance just calls
// toFixed(2)), so cash keeps a local en-US/USD formatter rather than inventing a
// shared helper; ticketz reuses the same `I18n.abbreviatedNumber` MainHeader uses.
const cashFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// `I18n.abbreviatedNumber(0)` returns '-' by Skillz HUD convention (a dash means
// "none" in-game). On the partner wallet strip a real 0 balance should read as
// "0" — a dash there looks like a broken/missing value — so anything that
// truncates to zero renders '0' explicitly; nonzero values keep the abbreviation
// (e.g. 915000 -> "915K").
function formatTicketz(value: number): string {
  return Math.trunc(value) === 0 ? '0' : I18n.abbreviatedNumber(value);
}

const valueClassByTone = {
  onDark: 'text-white',
  onLight: 'text-[#15171b]',
} as const;

export default function MulticadeBalanceBar({
  cashBalance,
  ticketzBalance,
  tone = 'onDark',
}: MulticadeBalanceBarProps) {
  // Neither balance known means "we don't know" (signed out / fetch failed) — show
  // nothing rather than authoritative-looking zeroes.
  if (cashBalance === undefined && ticketzBalance === undefined) return null;

  const valueClass = `text-[12px] font-extrabold leading-none ${valueClassByTone[tone]}`;

  return (
    <span className='flex items-center gap-3'>
      {ticketzBalance !== undefined ? (
        <span aria-label={`Ticketz balance ${ticketzBalance}`} className='flex items-center gap-1.5'>
          {/* eslint-disable-next-line @next/next/no-img-element -- static bundled currency icon */}
          <img src='/ticketz.svg' alt='' aria-hidden className='h-[15px] w-auto' />
          <span className={valueClass}>{formatTicketz(ticketzBalance)}</span>
        </span>
      ) : null}
      {cashBalance !== undefined ? (
        <span
          aria-label={`Cash balance ${cashFormatter.format(cashBalance)}`}
          className='flex items-center gap-1.5'
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- static bundled currency icon */}
          <img src='/cash.svg' alt='' aria-hidden className='h-[15px] w-auto' />
          <span className={valueClass}>{cashFormatter.format(cashBalance)}</span>
        </span>
      ) : null}
    </span>
  );
}
